import React, { useState, useEffect } from 'react';
import { Input, Button, Space, message, Form as AntForm, InputNumber, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';
import { getUnits, createUnit, updateUnit, deleteUnit } from '../../api/units.api';

import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';
import ConfirmDelete from '../../components/common/ConfirmDelete';

const UnitMasterPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [searchText, setSearchText] = useState('');

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  
  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.RAW_MATERIAL);

  const { control, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      unitName: '',
      purchaseUnit: '',
      consumptionUnit: '',
      mFactor: 1
    }
  });

  const mFactorValue = watch('mFactor') || '{mfactor}';
  const purchaseUnitValue = watch('purchaseUnit') || 'Purchase Unit';
  const consumptionUnitValue = watch('consumptionUnit') || 'Consumption Units';

  
  const { data: units = [], isLoading: isFetching } = useQuery({
    queryKey: ['units', plantId, searchText],
    queryFn: () => getUnits(plantId, { search: searchText }),
    enabled: !!plantId,
  });

  
  const createMutation = useMutation({
    mutationFn: (data) => createUnit(plantId, data),
    onSuccess: () => {
      message.success('Unit created successfully!');
      queryClient.invalidateQueries({ queryKey: ['units'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(err.message.includes('exists') ? 'Unit already exists' : err.message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateUnit(plantId, editingUnit.unitID, data),
    onSuccess: () => {
      message.success('Unit updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['units'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(err.message.includes('exists') ? 'Unit already exists' : err.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (uid) => deleteUnit(plantId, uid),
    onSuccess: () => {
      message.success('Unit deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
    onError: (err) => {
      message.error(err.message || 'Cannot delete unit. It might be in use.');
    }
  });

  
  const openDrawer = (record = null) => {
    setEditingUnit(record);
    if (record) {
      reset({
        unitName: record.unitName,
        purchaseUnit: record.purchaseUnit,
        consumptionUnit: record.consumptionUnit,
        mFactor: record.mFactor
      });
    } else {
      reset({ unitName: '', purchaseUnit: '', consumptionUnit: '', mFactor: 1 });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingUnit(null);
    reset();
  };

  const onSubmit = (data) => {
    if (editingUnit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  
  const columns = [
    { title: '#', dataIndex: 'index', key: 'index', render: (text, record, index) => index + 1, width: 60 },
    { title: 'Unit Name', dataIndex: 'unitName', key: 'unitName' },
    { title: 'Purchase Unit', dataIndex: 'purchaseUnit', key: 'purchaseUnit' },
    { title: 'Consumption Unit', dataIndex: 'consumptionUnit', key: 'consumptionUnit' },
    { title: 'M-Factor', dataIndex: 'mFactor', key: 'mFactor' },
    { title: 'Created On', dataIndex: 'createdOn', key: 'createdOn', render: (date) => formatDate(date) },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <ConfirmDelete 
              itemName={record.purchaseUnit} 
              onConfirm={() => deleteMutation.mutate(record.unitID)}
              loading={deleteMutation.isPending && deleteMutation.variables === record.unitID}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Unit Master" 
        subtitle="Manage units of measurement and conversions"
        onAdd={() => openDrawer(null)} 
        canAdd={canAdd} 
        addLabel="Add Unit"
      />

      <div style={{ marginBottom: 16, maxWidth: 400 }}>
        <Input.Search 
          placeholder="Search by unit name..." 
          allowClear 
          onSearch={(val) => setSearchText(val)}
          autoComplete="off"
        />
      </div>

      <DataTable 
        columns={columns} 
        data={units} 
        loading={isFetching} 
        rowKey="unitID"
      />

      <FormDrawer
        title={editingUnit ? "Edit Unit" : "Add Unit"}
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSubmit(onSubmit)}
        loading={createMutation.isPending || updateMutation.isPending}
        isDirty={isDirty}
        width={400}
      >
        <AntForm layout="vertical">
          <AntForm.Item label="Unit Name">
            <Controller
              name="unitName"
              control={control}
              render={({ field }) => <Input {...field} placeholder="e.g. Kilogram/Gram" autoComplete="off" />}
            />
          </AntForm.Item>

          <AntForm.Item 
            label="Purchase Unit" 
            validateStatus={errors.purchaseUnit ? 'error' : ''} 
            help={errors.purchaseUnit?.message}
            required
          >
            <Controller
              name="purchaseUnit"
              control={control}
              rules={{ required: 'Purchase unit is required' }}
              render={({ field }) => (
                <Input 
                  {...field} 
                  placeholder="e.g. KG" 
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())} 
                  autoComplete="off"
                />
              )}
            />
          </AntForm.Item>

          <AntForm.Item 
            label="Consumption Unit" 
            validateStatus={errors.consumptionUnit ? 'error' : ''} 
            help={errors.consumptionUnit?.message}
            required
          >
            <Controller
              name="consumptionUnit"
              control={control}
              rules={{ required: 'Consumption unit is required' }}
              render={({ field }) => (
                <Input 
                  {...field} 
                  placeholder="e.g. GRM" 
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())} 
                  autoComplete="off"
                />
              )}
            />
          </AntForm.Item>

          <AntForm.Item 
            label="M-Factor (Conversion)" 
            validateStatus={errors.mFactor ? 'error' : ''} 
            help={errors.mFactor ? errors.mFactor.message : `1 ${purchaseUnitValue} = ${mFactorValue} ${consumptionUnitValue}`}
            required
          >
            <Controller
              name="mFactor"
              control={control}
              rules={{ 
                required: 'M-Factor is required',
                min: { value: 0.0001, message: 'Must be greater than 0' }
              }}
              render={({ field }) => (
                <InputNumber 
                  {...field} 
                  style={{ width: '100%' }} 
                  placeholder="e.g. 1000" 
                  min={0.0001}
                />
              )}
            />
          </AntForm.Item>
        </AntForm>
      </FormDrawer>
    </div>
  );
};

export default UnitMasterPage;